import { useMemo, useState } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router'

import {
  Flex,
  TabsComponent as Tabs,
  TertiaryButton,
} from '@enechain/ecloud-designsystem'
import { FaArrowUpFromBracketRegular } from '@enechain/ecloud-icons'

import { DsSelect } from '~/components/common/DsSelect'
import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PlaceholderPage } from '~/components/common/PlaceholderPage'
import { RegionLoading } from '~/components/common/RegionLoading'
import {
  FilterSection,
  FilterToolbar,
} from '~/components/common/filters/FilterToolbar'
import { iconSize } from '~/constants/iconSize'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { convertAreaLabel } from '~/models/area'
import { withCsvImportReturnTo } from '~/models/csvImportReturn'
import { deriveVisibleAreaCodes } from '~/pages/AdminLossRate/models/visibleAreas'
import { parseAreaCodeParam } from '~/pages/DemandForecastDaily/models/sectionHelpers'
import { useDemandGroupDirectory } from '~/pages/DemandSettings/hooks/useDemandGroupDirectory'
import { DemandGroupsTab } from '~/pages/DemandSettings/tabs/DemandGroupsTab'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'
import { routePaths } from '~/routes/routePaths'

/** URL `?tab=` に載せるタブの識別子。段階リリースで出すタブが増えたらここに足す。 */
export type DemandSettingsTabKey = 'demand-groups'

/** URL の `?tab=` 名 */
export const DEMAND_SETTINGS_TAB_PARAM = 'tab'

/** URL の `?area=<AreaCode の値>` 名。CSV 取り込みから戻った時に選んでいたエリアで開くために使う */
export const DEMAND_SETTINGS_AREA_PARAM = 'area'

/**
 * 需要想定設定画面 (`/demand/settings`、AC-DFS-001〜003)。エリアの切り替え (全タブ共通) の下に、
 * 出せるタブだけを並べる。今出せるのは「需要グループ」だけで、タブが 1 つでも見出しは出す。
 * 選んだタブは URL の `?tab=` で表す (不正・未指定は出せる最初のタブ)。
 *
 * 需要グループの公開は組織ごとの feature flag: 目録が `featureEnabled=false` の組織は出せる
 * タブが無いので、画面全体を PlaceholderPage にする (評価失敗も backend が false に畳む)。
 */
export const DemandSettingsPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [chosenArea, setChosenArea] = useState<AreaCode | undefined>(undefined)

  const directory = useDemandGroupDirectory()
  const personalSettings = useGetPersonalSettingsQuery()

  // エリアの選択肢 = 業務エリア設定 ∩ 全エリア (業務エリア設定の順)。
  // 初期値は URL の `?area=` が選択肢に含まれればそれ、無い・不正なら先頭。
  // 画面での切り替えは URL へ書き戻さない (DemandForecastDaily と同じ。戻り先は導線を作る時点のエリアを使う)。
  const urlArea = parseAreaCodeParam(
    searchParams.get(DEMAND_SETTINGS_AREA_PARAM),
  )
  const visibleAreas = deriveVisibleAreaCodes(
    personalSettings.data?.settings?.businessAreas ?? [],
  )
  const area =
    chosenArea !== undefined && visibleAreas.includes(chosenArea)
      ? chosenArea
      : urlArea !== undefined && visibleAreas.includes(urlArea)
        ? urlArea
        : visibleAreas[0]

  const tabs = useMemo<Array<{ key: DemandSettingsTabKey; label: string }>>(
    () => [
      {
        key: 'demand-groups',
        label: t('page:demandSettings.tab.demandGroups'),
      },
    ],
    [t],
  )
  const tabParam = searchParams.get(DEMAND_SETTINGS_TAB_PARAM)
  const tab = tabs.find((candidate) => candidate.key === tabParam) ?? tabs[0]

  // 公開の有無は目録で分かる。取得が終わるまでは、タブもエリアも出さず画面全体を読み込み中にする
  // (未公開の組織にタブ見出しが一瞬出てから開発中の表示へ替わるのを避ける、AC-DFS-003)
  if (directory.isLoading) {
    return (
      <PageLayout
        breadcrumbItems={[
          { label: t('page:demandSettings.breadcrumb.demand') },
          { label: t('common:route.demandSettings'), bold: true },
        ]}
      >
        <PageBody>
          <RegionLoading flex="1" isLoading>
            {null}
          </RegionLoading>
        </PageBody>
      </PageLayout>
    )
  }

  if (directory.data?.featureEnabled === false) {
    return <PlaceholderPage routeKey="demandSettings" />
  }

  // CSV 取り込みの戻り先には今選んでいるエリアを載せる (URL の area は初期値で、画面で切り替えても書き戻さないため)
  const returnParams = new URLSearchParams({ tab: tab.key })
  if (area !== undefined)
    returnParams.set(DEMAND_SETTINGS_AREA_PARAM, String(area))
  const csvImportLink = withCsvImportReturnTo(
    routePaths.adminUploadsDemandGroups.path,
    `${routePaths.demandSettings.path}?${returnParams.toString()}`,
  )

  const handleSelectTab = (key: DemandSettingsTabKey): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set(DEMAND_SETTINGS_TAB_PARAM, key)
        return next
      },
      { replace: true },
    )
  }

  return (
    <PageLayout
      breadcrumbItems={[
        { label: t('page:demandSettings.breadcrumb.demand') },
        { label: t('common:route.demandSettings'), bold: true },
      ]}
    >
      <PageBody>
        <FilterToolbar
          resetAction={
            tab.key === 'demand-groups' ? (
              <TertiaryButton asChild size="md">
                <RouterLink to={csvImportLink}>
                  <FaArrowUpFromBracketRegular
                    boxSize={iconSize.labeledButton}
                  />
                  {t('page:adminDemandGroups.toolbar.csvUpload')}
                </RouterLink>
              </TertiaryButton>
            ) : undefined
          }
        >
          <FilterSection label={t('page:demandSettings.areaLabel')}>
            <DsSelect
              minWidth="221px"
              aria-label={t('page:demandSettings.areaLabel')}
              inputId="demandSettings-area"
              onChange={(value) => setChosenArea(Number(value) as AreaCode)}
              options={visibleAreas.map((code) => ({
                value: String(code),
                label: convertAreaLabel(code),
              }))}
              value={area === undefined ? '' : String(area)}
            />
          </FilterSection>
        </FilterToolbar>
        <Tabs
          items={tabs}
          onSelect={handleSelectTab}
          selected={tab.key}
          tabSx={{ fontSize: tabTriggerFontSize.raw }}
        />
        <Flex flexDirection="column" flex="1" minHeight="0">
          {tab.key === 'demand-groups' && (
            <DemandGroupsTab
              area={area}
              directory={directory}
              isAreaLoading={personalSettings.isLoading}
            />
          )}
        </Flex>
      </PageBody>
    </PageLayout>
  )
}
